import { aimX, aimY } from "../combat/aim.ts";
import { BLOCKING, canStand, type CollisionGrid } from "./step.ts";

/**
 * A dash's path (ADR-073's amendment of 2026-10-02, item 4; Bible 13.6): a straight segment from the caster's feet to
 * the point its length along the aim, walked as lattice points, each the start plus the segment scaled by `i / n` and
 * rounded, `n` the segment's longer axis in sixteenths, so a step moves at most one sixteenth on each axis and never
 * tunnels through a wall. Each tick of the dash walks on to its goal, the point `floor(k * n / ticks)`, testing every
 * point with the walk's own stand test, and stops for good at the first it cannot stand on, flush against what blocks
 * it and never sliding along it. The map server runs it; a page that predicts the dash would run it too.
 */

/**
 * Where a dash stands and how far along its path it has come, written in place.
 */
export interface DashCursor {
  x: number;
  y: number;
  index: number;
}

/**
 * A cursor at a dash's start.
 *
 * @param x The start's x in sixteenths.
 * @param y The start's y.
 * @returns The cursor, at point 0.
 */
export function newDashCursor(x: number, y: number): DashCursor {
  return { x, y, index: 0 };
}

/**
 * Writes a dash's end: the start plus its length along the aim, each axis rounded to the nearest sixteenth.
 *
 * @param out Where the end is written.
 * @param x The start's x in sixteenths.
 * @param y The start's y.
 * @param aim The aim, 0 to 65,535 (ADR-073 decision 7).
 * @param length The dash's length in sixteenths.
 */
export function dashEnd(out: { x: number; y: number }, x: number, y: number, aim: number, length: number): void {
  out.x = x + Math.round(aimX(aim) * length);
  out.y = y + Math.round(aimY(aim) * length);
}

/**
 * Walks a dash on to its goal for a tick: from the cursor's point toward point `floor(k * n / ticks)` of the path, a
 * lattice point at a time, each tested with `canStand` under the mask. The cursor stays at the last point it could
 * stand on.
 *
 * @param cursor The dash's cursor, moved in place.
 * @param startX The path's start x in sixteenths.
 * @param startY Its start y.
 * @param endX Its end x.
 * @param endY Its end y.
 * @param k The dash's tick, 1 for its first.
 * @param ticks The ticks the whole path takes.
 * @param grid The live collision grid.
 * @param mask The bits that stop it; `BLOCKING` by default, as a walk's.
 * @returns False when a point it could not stand on stopped it, so the dash ends; true otherwise.
 */
export function advanceDash(cursor: DashCursor, startX: number, startY: number, endX: number, endY: number, k: number, ticks: number, grid: CollisionGrid, mask: number = BLOCKING): boolean {
  const dx = endX - startX;
  const dy = endY - startY;
  const n = Math.max(Math.abs(dx), Math.abs(dy));
  const goal = Math.min(n, Math.floor((k * n) / ticks));
  for (let i = cursor.index + 1; i <= goal; i += 1) {
    const x = startX + Math.round((i * dx) / n);
    const y = startY + Math.round((i * dy) / n);
    if (!canStand(x, y, grid, mask)) {
      return false;
    }
    cursor.x = x;
    cursor.y = y;
    cursor.index = i;
  }
  return true;
}
